// src/components/featureAdjudication/CellMap.jsx
//
// The cell map. Forked from wellfinder/ReviewMap.jsx: same lifecycle, same tile
// auth, same "panel inside a workflow, not an exploration surface" argument.
//
// ── FIT-BOUNDS, WHICH REVERSES ReviewMap'S DECISION ON PURPOSE ──────────────
//
// ReviewMap argues for a FIXED zoom, and it is right for what it does: its
// subject is a point with a 100 m candidate radius, the viewport always contains
// that radius, and an analyst crossing thousands of records needs their eye to
// learn ONE scale so that a 5 m offset looks like 5 m every time.
//
// Neither half of that holds here.
//
// The subject is not a point, it is an EXTENT, and the extent this map has to
// frame -- the deferral plus its rivals -- was measured across all 229 rows:
//
//    1.3 -  8.9 m   65.1%
//   10.5 - 17.6 m   23.6%
//          56.9 m    0.9%
//          74.0 m   10.5%
//
// Four orders of magnitude in area (0.4 m2 to 2639 m2). There is no single scale
// to learn, because the thing being judged is what varies. And at z20 the
// vertical viewport is +/-17.8 m on a 560 px pane, so a fixed z20 silently CROPS
// the 74 m cases -- 10.5% of the corpus, which is the one cluster where getting
// it wrong is most expensive.
//
// The volume argument inverts too: 103 decisions, not 9,348, so the per-record
// cost of re-reading the scale is affordable in a way it would not be there.
//
// MAX_FIT_ZOOM caps the other end. The smallest deferral is ~0.63 m across --
// 2.5 px at z18, 10 px at z20 -- and 10 px of outline is about the least a human
// can judge a boundary from. Past z20 it is pure upscaling: BASEMAP_10CM is
// 10 cm/px, so there is no new information after ~z19.3.

import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../../lib/map/whenMapLoaded";
import { getOrFetchTileToken, bindTileAuthHandler } from "../../api/tileTokens";
import { tileUrl } from "../../api/tileUrl";
import { attachAttribution, basemapAttribution, SATELLITE_STYLE_ID } from "../../lib/map/attribution";
import { buildTransformRequest } from "../../api/mapTransformRequest";
import { useAuthStore } from "../../stores/useAuthStore";
import { getConfig } from "../../config";
import {
  polygonLinePaint, polygonFillPaint, candidateCirclePaint, cellLinePaint,
  MAX_FIT_ZOOM, FIT_PADDING_PX,
} from "./featureMapEncoding";

const SRC = { imagery: "fa-imagery", cell: "fa-cell", polys: "fa-polys", cands: "fa-cands" };
const EMPTY_FC = { type: "FeatureCollection", features: [] };

/**
 * Add the imagery source, or RETARGET it when the dataset changes.
 *
 * ── THE BUG THIS FIXES ─────────────────────────────────────────────────────
 *
 * This was `if (!existing) { add }` with no else. When datasetKey changed the
 * effect re-ran, found the source already present, and did nothing -- the
 * previous cell's tiles stayed under the new cell's geometry.
 *
 * It was invisible for two independent reasons, and BOTH are about to stop
 * being true. The page unmounts between cells today, so the map is destroyed
 * and rebuilt; that ends when the list and detail collapse into one page. And
 * all 14 cells currently resolve to one dataset
 * (imagery.permian.midland.2025_12_13), so datasetKey never actually changes;
 * that ends when a second collect lands.
 *
 * wellfinder/ReviewMap.jsx -- the component this was forked from -- has the
 * else branch and always did. It was dropped in the fork because unmount made
 * it unnecessary, which is exactly the kind of correctness that stops being
 * correct when the structure around it moves.
 *
 * Extracted as a function rather than left inline so it can be tested against a
 * fake map. The inline version was unreachable from a test without standing up
 * MapLibre in jsdom, which is why a missing branch survived review.
 *
 * @returns {"added"|"retargeted"|"unchanged"} what it did, for the test and for
 *   anyone debugging why tiles did or did not move.
 */
export function applyImagerySource(map, url, beforeLayerId) {
  const existing = map.getSource(SRC.imagery);
  if (!existing) {
    map.addSource(SRC.imagery, { type: "raster", tiles: [url], tileSize: 256, maxzoom: 20 });
    map.addLayer({ id: "fa-imagery-raster", type: "raster", source: SRC.imagery }, beforeLayerId);
    return "added";
  }
  // Compared on the URL, not on datasetKey: the url carries `ck` too, and a ck
  // that changed while the dataset did not still means different tiles.
  if (existing.tiles?.[0] !== url) {
    existing.setTiles([url]);
    return "retargeted";
  }
  return "unchanged";
}

/** Bounding box of a GeoJSON geometry, folded into an existing one. */
function extend(bounds, geom) {
  if (!geom) return bounds;
  const walk = (c) => {
    if (typeof c[0] === "number") {
      bounds[0] = Math.min(bounds[0], c[0]); bounds[1] = Math.min(bounds[1], c[1]);
      bounds[2] = Math.max(bounds[2], c[0]); bounds[3] = Math.max(bounds[3], c[1]);
    } else c.forEach(walk);
  };
  walk(geom.coordinates);
  return bounds;
}

export default function CellMap({ group, subjectId, decided, imagery, cellGeom, cellKey, onSelectMember }) {
  const holder = useRef(null);
  const mapRef = useRef(null);
  const readyRef = useRef(false);
  // Handlers change every render; map listeners bind once. A ref keeps the
  // binding stable without re-registering per keystroke.
  const cb = useRef({});
  cb.current = { onSelectMember };

  const datasetKey = imagery?.dataset_key ?? null;

  useEffect(() => {
    if (!holder.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: holder.current,
      style: {
        version: 8,
        sources: {
          "mapbox-satellite": {
            type: "raster",
            tiles: [`https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${getConfig().mapboxToken}`],
            tileSize: 256,
            attribution: basemapAttribution(SATELLITE_STYLE_ID),
          },
        },
        layers: [{ id: "mapbox-satellite", type: "raster", source: "mapbox-satellite" }],
      },
      center: [-102, 32],
      zoom: 16,
      attributionControl: { compact: false },
      maxzoom: MAX_FIT_ZOOM,
      transformRequest: buildTransformRequest(() => useAuthStore.getState().token),
    });
    // Mapbox's logo and credit (REM-684).
    attachAttribution(map, { mapboxSource: "mapbox-satellite" });
    mapRef.current = map;
    // Matches the __mapA / __mapB / __mapReview convention so the same debugging
    // habits work here.
    window.__mapFeatures = map;
    bindTileAuthHandler(map);

    whenMapLoaded(map, () => {
      readyRef.current = true;
      map.addSource(SRC.cell,  { type: "geojson", data: EMPTY_FC });
      map.addSource(SRC.polys, { type: "geojson", data: EMPTY_FC });
      map.addSource(SRC.cands, { type: "geojson", data: EMPTY_FC });

      // Draw order is the encoding's argument made concrete: imagery, then the
      // cell frame, then the wash, then boundaries, then the contested points on
      // top of everything. A candidate under a boundary line is the one thing
      // that must never happen -- it is what the decision is about.
      map.addLayer({ id: "fa-cell-line",  type: "line", source: SRC.cell,  paint: cellLinePaint() });
      map.addLayer({ id: "fa-polys-fill", type: "fill", source: SRC.polys, paint: polygonFillPaint() });
      map.addLayer({ id: "fa-polys-line", type: "line", source: SRC.polys, paint: polygonLinePaint() });
      map.addLayer({ id: "fa-cands-pt",   type: "circle", source: SRC.cands, paint: candidateCirclePaint() });

      map.on("click", "fa-polys-line", (e) => {
        const id = e.features?.[0]?.properties?.observation_id;
        if (id) cb.current.onSelectMember?.(id);
      });
      map.on("mouseenter", "fa-polys-line", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "fa-polys-line", () => { map.getCanvas().style.cursor = ""; });
    });

    return () => { try { map.remove(); } catch { /* already gone */ } mapRef.current = null; readyRef.current = false; };
  }, []);

  // ── Imagery ────────────────────────────────────────────────────────────────
  // THE ONLY TILE PATH. ck comes from GET /tile/session via the token cache, and
  // is in the URL because that is what the nginx cache key is built from.
  //
  // A null datasetKey is a REAL STATE -- no entitled visual imagery covers this
  // location -- and the map keeps the Mapbox basemap rather than showing blank
  // tiles that read as a loading failure.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !datasetKey) return;
    let cancelled = false;
    (async () => {
      try {
        const { ck } = await getOrFetchTileToken(datasetKey);
        if (cancelled || !mapRef.current) return;
        const url = tileUrl(datasetKey, ck);
        const apply = () => applyImagerySource(map, url, "fa-cell-line");
        if (readyRef.current) apply(); else map.once("load", apply);
      } catch { /* the basemap stands in */ }
    })();
    return () => { cancelled = true; };
  }, [datasetKey]);

  // ── Clearing between cells ─────────────────────────────────────────────────
  //
  // Emptying the three geojson sources the moment the cell changes, BEFORE the
  // new /queue response lands. Without this the previous cell's polygons hang
  // over the new cell's imagery for the duration of the fetch, which reads as
  // "these boundaries are here" rather than as "still loading" -- and on a map
  // whose entire question is which boundary owns a point, that is the worst
  // possible thing to render.
  //
  // Also fires when `group` goes null with the cell unchanged, which happens
  // when every group in a cell is decided. Today the page unmounts between
  // cells so neither case is reachable; both become reachable the moment the
  // list and the detail share one page.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    if (group) return;                       // there is something to draw; the effect below draws it
    map.getSource(SRC.polys)?.setData(EMPTY_FC);
    map.getSource(SRC.cands)?.setData(EMPTY_FC);
    map.getSource(SRC.cell)?.setData(EMPTY_FC);
  }, [cellKey, group]);

  // ── The group ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !group) return;

    const paint = () => {
      const polys = [];
      const cands = [];
      let bounds = [180, 90, -180, -90];

      for (const m of group.members) {
        polys.push({
          type: "Feature",
          geometry: m.geom,
          properties: {
            observation_id: m.observation_id,
            role: m.observation_id === subjectId ? "subject" : "rival",
            active: m.observation_id === subjectId ? 0 : 1,
            decided: decided[m.observation_id] ? 1 : 0,
          },
        });
        bounds = extend(bounds, m.geom);
        for (const c of m.candidate_points ?? []) {
          cands.push({ type: "Feature", geometry: c.geom, properties: { observation_id: c.observation_id } });
        }
        // Rival polygons that are NOT themselves group members -- 2 of 258 edges
        // point outside this cell's queue. Drawn, because the boundary is real
        // and the reviewer is judging against it, but never selectable.
        for (const r of m.rival_polygons ?? []) {
          if (!group.members.some((g) => g.observation_id === r.observation_id)) {
            polys.push({
              type: "Feature", geometry: r.geom,
              properties: { observation_id: r.observation_id, role: "rival", active: 0, decided: 0 },
            });
            bounds = extend(bounds, r.geom);
          }
        }
      }

      map.getSource(SRC.polys)?.setData({ type: "FeatureCollection", features: polys });
      map.getSource(SRC.cands)?.setData({ type: "FeatureCollection", features: cands });
      map.getSource(SRC.cell)?.setData(
        cellGeom ? { type: "Feature", geometry: cellGeom, properties: {} } : EMPTY_FC);

      if (bounds[0] <= bounds[2]) {
        map.fitBounds([[bounds[0], bounds[1]], [bounds[2], bounds[3]]], {
          padding: FIT_PADDING_PX, maxZoom: MAX_FIT_ZOOM, duration: 0,
        });
      }
    };

    if (readyRef.current) paint(); else map.once("load", paint);
  }, [group, subjectId, decided, cellGeom]);

  return <div ref={holder} className="h-full w-full" data-testid="fa-cell-map" />;
}
